"use client";

import type React from "react";
import { Component, type ErrorInfo, type ReactNode } from "react";
import { safeStringify } from "@/utils/api";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  componentName?: string; // Optional component name for better error reporting
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
}

class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: undefined,
    errorInfo: undefined,
  };

  // Update state so the next render will show the fallback UI
  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  // Log the error details
  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Enhanced error logging
    console.error("=== ErrorBoundary Caught an Error ===");
    console.error("Component:", this.props.componentName || "Unknown");
    console.error("Error message:", safeStringify(error.message));
    console.error("Error stack:", safeStringify(error.stack));
    console.error("Component stack:", safeStringify(errorInfo.componentStack));
    console.error("URL:", window.location.href);

    this.setState({
      error,
      errorInfo,
    });
  }

  public render() {
    if (this.state.hasError) {
      // Use default fallback if none provided
      const fallback = this.props.fallback || (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
          <h3 className="text-red-800 font-bold mb-2">Something went wrong.</h3>
          {process.env.NODE_ENV === "development" && (
            <>
              <p className="text-red-600 mb-2">{this.state.error?.message}</p>
              <details className="text-sm text-gray-700">
                <summary className="cursor-pointer mb-2">View technical details</summary>
                {this.state.error?.stack && (
                  <pre className="whitespace-pre-wrap break-words bg-white p-4 rounded border">
                    {this.state.error.stack}
                  </pre>
                )}
                {this.state.errorInfo?.componentStack && (
                  <pre className="whitespace-pre-wrap break-words bg-white p-4 rounded border mt-2">
                    {this.state.errorInfo.componentStack}
                  </pre>
                )}
              </details>
            </>
          )}
        </div>
      );

      return fallback;
    }

    return this.props.children;
  }
}

// Higher-Order Component (HOC) for easier usage with components
export function withErrorBoundary<P extends object>(
  WrappedComponent: React.ComponentType<P>,
  componentName: string,
  Fallback?: ReactNode,
) {
  const displayName = WrappedComponent.displayName || WrappedComponent.name || "Component";

  return function WithErrorBoundaryWrapper(props: P) {
    return (
      <ErrorBoundary componentName={componentName || displayName} fallback={Fallback}>
        <WrappedComponent {...props} />
      </ErrorBoundary>
    );
  };
}

export default ErrorBoundary;
